import React, { useEffect, useState } from "react";
import { useAuth, ROLE_LABELS } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input, Label } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast, Toaster } from "sonner";

export default function ProfilePage() {
  const { user, refreshMe } = useAuth();
  const [form, setForm] = useState({ name: "", phone: "", department: "", job_title: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        phone: user.phone || "",
        department: user.department || "",
        job_title: user.job_title || "",
      });
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/profile", form);
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const initials =
    user?.name?.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "U";

  return (
    <div className="max-w-4xl space-y-6">
      <Toaster richColors position="top-right" />

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Your profile</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage your personal information and how it appears across CCAMS.
        </p>
      </div>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-semibold">
              {initials}
            </div>
            <div>
              <div className="text-lg font-semibold">{user?.name}</div>
              <div className="text-sm text-muted-foreground">{user?.email}</div>
              <div className="mt-1">
                <Badge variant="primary" data-testid="profile-role">{ROLE_LABELS[user?.role]}</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Personal details</CardTitle>
          <CardDescription>These details are visible to your organisation.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Full name</Label>
              <Input
                data-testid="profile-name"
                className="mt-1.5"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input className="mt-1.5" value={user?.email || ""} disabled />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                data-testid="profile-phone"
                className="mt-1.5"
                value={form.phone || ""}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Job title</Label>
              <Input
                data-testid="profile-title"
                className="mt-1.5"
                value={form.job_title || ""}
                onChange={(e) => setForm({ ...form, job_title: e.target.value })}
              />
            </div>
            <div>
              <Label>Department</Label>
              <Input
                data-testid="profile-department"
                className="mt-1.5"
                value={form.department || ""}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
              />
            </div>
            <div className="md:col-span-2 flex justify-end">
              <Button type="submit" data-testid="profile-save" disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
